import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Nav from "../../../components/Nav";
import PayWidgetEvents from "../../../components/PayWidgetEvents";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  clearCombineBundleCheckoutStorage,
  getCombineBundleSuccessMessage,
  registerAndPurchaseCombineBundle,
} from "../../../services/PaymentService";
import { registerAccountStore } from "../../../store/slices/accountSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleMinus,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

// Field names match the GET /payment/combine-bundle/{eventId} contract
// (CombineBundleOfferVM). Every price, total and saving shown here comes
// straight from the route — nothing is recalculated in the browser, except
// the "Single Event + Position Drill" price (combine + drill of the selected
// combine), summed the same way the plans screen prices it.
const getBundleId = (bundle) => bundle?.combineBundleId;
const getBundleName = (bundle) => bundle?.name ?? "Combine Bundle";
const getCombines = (bundle) =>
  Array.isArray(bundle?.combines) ? bundle.combines : [];
const getBundleCombineCount = (bundle) =>
  bundle?.combineCount ?? getCombines(bundle).length;
const getTotalSeparatePrice = (bundle) =>
  Number(bundle?.totalIfBoughtSeparately ?? 0);
// A percent-off bundle price can come back with more than 2 decimals
// (e.g. 134.388), which IntelliPay shows as $0, so it is rounded to cents.
const getBundlePrice = (bundle) =>
  Math.round((Number(bundle?.bundlePrice ?? 0) + Number.EPSILON) * 100) / 100;
// getBundlePrice reads a missing price as 0, which would register the bundle
// through the free path, so the offer is only shown for a real price.
const hasValidBundlePrice = (bundle) => {
  const raw = bundle?.bundlePrice;
  if (raw === null || raw === undefined || raw === "") return false;
  const price = Number(raw);
  return Number.isFinite(price) && price >= 0;
};
const getSavingsAmount = (bundle) => Number(bundle?.savingsAmount ?? 0);
const getSavingsPercent = (bundle) => Number(bundle?.savingsPercent ?? 0);
const getDrillCombineCount = (bundle) =>
  bundle?.combinesWithPositionDrillsCount ??
  getCombines(bundle).filter(getHasPositionDrills).length;

const getCombineEventId = (combine) => combine?.eventId;
const getCombineName = (combine) => combine?.name ?? "Combine";
const getCombineLocation = (combine) =>
  [combine?.city, combine?.state].filter(Boolean).join(", ");
const getCombinePrice = (combine) => Number(combine?.combinePrice ?? 0);
const getHasPositionDrills = (combine) => Boolean(combine?.hasPositionDrills);
const getPositionDrillPrice = (combine) =>
  Number(combine?.positionDrillPrice ?? 0);
const isSelectedCombine = (combine) => Boolean(combine?.isSelected);

const formatMoney = (value) => `$${Number(value ?? 0).toFixed(2)}`;
const formatSavings = (value) => {
  const amount = Number(value ?? 0);
  return Number.isInteger(amount) ? `$${amount}` : formatMoney(amount);
};
const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const pad2 = (value) => String(value ?? 0).padStart(2, "0");

const formatDate = (date) => {
  if (!date) return "";
  if (typeof date === "object") {
    if (typeof date.year !== "number") return "";
    return `${pad2(date.month)}-${pad2(date.day)}-${date.year}`;
  }
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return "";
  return `${month}-${day}-${year}`;
};

const formatTime = (time) => {
  if (!time) return "";
  let hour;
  let minute;
  if (typeof time === "object") {
    hour = time.hour;
    minute = time.minute;
  } else {
    [hour, minute] = String(time).split(":").map(Number);
  }
  if (typeof hour !== "number" || Number.isNaN(hour)) return "";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour >= 12 ? "PM" : "AM";
  return `${hour12}:${pad2(Number.isNaN(minute) ? 0 : minute)} ${period}`;
};

const getCombineDate = (combine) => formatDate(combine?.date);

const getCombineTime = (combine) => {
  const start = formatTime(combine?.startTime);
  const end = formatTime(combine?.endTime);
  if (start && end) return `${start} - ${end}`;
  return start || end;
};

const Icon = ({ className = "h-4 w-4", children }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`flex-shrink-0 ${className}`}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const CalendarIcon = ({ className }) => (
  <Icon className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </Icon>
);

const ClockIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </Icon>
);

const PinIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Icon>
);

const TrophyIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </Icon>
);

const TagIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r="1" />
  </Icon>
);

const DollarIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8M12 18V6" />
  </Icon>
);

const InfoIcon = ({ className }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-5M12 8h.01" />
  </Icon>
);

const UsersIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </Icon>
);

const ArrowLeftIcon = ({ className }) => (
  <Icon className={className}>
    <path d="m12 19-7-7 7-7M19 12H5" />
  </Icon>
);

const CloseIcon = ({ className }) => (
  <Icon className={className}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

const CheckCircle = ({ className = "h-8 w-8" }) => (
  <span
    className={`flex-shrink-0 rounded-full bg-blue-600 text-white flex items-center justify-center ${className}`}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-1/2 w-1/2"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  </span>
);

const STEPS = ["Offers Details", "T-Shirt Size", "Position", "Combine Bundle"];
const ACTIVE_STEP = STEPS.length - 1;

const StepChevron = () => (
  <svg
    viewBox="0 0 16 80"
    preserveAspectRatio="none"
    className="h-full w-4 flex-shrink-0 text-gray-200"
    aria-hidden="true"
  >
    <path
      d="M0 0 L16 40 L0 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const Stepper = () => (
  <div className="flex items-stretch border border-gray-200 rounded-lg overflow-hidden h-16 sm:h-20">
    {STEPS.map((label, index) => {
      const isActive = index === ACTIVE_STEP;
      return (
        <div key={label} className="flex flex-1 items-stretch min-w-0">
          {index > 0 && <StepChevron />}
          <div
            className={`flex flex-1 items-center justify-center gap-2 sm:gap-3 px-2 min-w-0 ${
              isActive ? "bg-blue-50" : ""
            }`}
          >
            {isActive ? (
              <span className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 rounded-full bg-blue-600 text-white font-bold text-base sm:text-lg flex items-center justify-center">
                {index + 1}
              </span>
            ) : (
              <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8" />
            )}
            <span
              className={`text-sm truncate ${
                isActive
                  ? "font-semibold text-blue-600 hidden sm:inline"
                  : "text-gray-800 hidden md:inline"
              }`}
            >
              {label}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

const CombineDetails = ({ combine }) => (
  <div className="text-sm text-gray-600 flex flex-wrap gap-x-5 gap-y-1 mt-1.5">
    {getCombineDate(combine) && (
      <span className="flex items-center gap-2">
        <CalendarIcon />
        {getCombineDate(combine)}
      </span>
    )}
    {getCombineTime(combine) && (
      <span className="flex items-center gap-2">
        <ClockIcon />
        {getCombineTime(combine)}
      </span>
    )}
    {getCombineLocation(combine) && (
      <span className="flex items-center gap-2">
        <PinIcon />
        {getCombineLocation(combine)}
      </span>
    )}
  </div>
);

const IncludedChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-green-50 text-gray-800 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-green-600" />
    {children}
  </span>
);

const OptionalChip = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 text-sm rounded px-2.5 py-1">
    <FontAwesomeIcon icon={faCircleMinus} className="h-4 w-4 text-gray-900" />
    {children}
  </span>
);

const BundleCombineRow = ({ combine, isSelected }) => (
  <div className="border border-gray-200 rounded-lg px-4 sm:px-6 py-3 bg-white">
    {isSelected && (
      <div className="text-xs font-semibold text-blue-700 uppercase mb-0.5">
        Your Selected Combine (Included)
      </div>
    )}
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
      <div className="min-w-0">
        <div className="font-semibold text-lg text-gray-900 break-words">
          {getCombineName(combine)}
        </div>
        <CombineDetails combine={combine} />
      </div>
      <div className="flex flex-col sm:items-end gap-1.5 flex-shrink-0">
        <span className="text-lg font-bold text-gray-900">
          {formatMoney(getCombinePrice(combine))}
        </span>
        {getHasPositionDrills(combine) && (
          <IncludedChip>
            Position Drill included (+
            {formatSavings(getPositionDrillPrice(combine))} value)
          </IncludedChip>
        )}
      </div>
    </div>
  </div>
);

const RadioOption = ({ value, checked, onChange, children }) => (
  <label
    className={`flex items-start gap-4 cursor-pointer rounded-lg border-2 px-4 sm:px-5 py-4 ${
      checked ? "border-blue-600 bg-blue-50/40" : "border-gray-200 bg-white"
    }`}
  >
    <input
      type="radio"
      name="eventOption"
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="mt-1 h-6 w-6 flex-shrink-0 accent-blue-600 cursor-pointer"
    />
    <div className="flex-1 min-w-0">{children}</div>
  </label>
);

const OPTION_SINGLE_WITH_DRILL = "singleWithDrill";
const OPTION_SINGLE = "single";
const OPTION_BUNDLE = "bundle";

const CombineBundleStep = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();

  const { eventId, bundle } = location.state || {};
  const combines = getCombines(bundle);
  const selectedCombine =
    combines.find(isSelectedCombine) ??
    combines.find(
      (combine) => String(getCombineEventId(combine)) === String(eventId),
    );
  const remainingCombines = combines.filter(
    (combine) => combine !== selectedCombine,
  );
  const selectedHasDrills = getHasPositionDrills(selectedCombine);
  const drillCombineCount = getDrillCombineCount(bundle);
  const combineCount = getBundleCombineCount(bundle);
  const savingsAmount = getSavingsAmount(bundle);
  const savingsPercent = Math.round(getSavingsPercent(bundle));

  const singlePrice = getCombinePrice(selectedCombine);
  const selectedDrillPrice = getPositionDrillPrice(selectedCombine);
  const singleWithDrillPrice = singlePrice + selectedDrillPrice;

  const [option, setOption] = useState(
    selectedHasDrills ? OPTION_SINGLE_WITH_DRILL : OPTION_SINGLE,
  );
  const [isIntellipayReady, setIsIntellipayReady] = useState(false);
  const [isRegisteringFree, setIsRegisteringFree] = useState(false);

  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const accounts = useSelector((state) => state.account.register);
  const storedAccount = useMemo(
    () => JSON.parse(localStorage.getItem("registeredAccount") || "{}"),
    [],
  );
  const user = accounts.length ? accounts[0] : storedAccount;

  const isBundleOption = option === OPTION_BUNDLE;
  const bundleId = getBundleId(bundle);
  const bundlePrice = getBundlePrice(bundle);
  // Only the bundle is paid on this screen; the single-event options keep the
  // plans screen, where a coupon can be entered (bundles take no coupon).
  const payBundleHere = isBundleOption && bundlePrice > 0;
  // Handed to PayWidgetEvents; its presence is what switches the approval
  // handler to the bundle purchase.
  const bundleChoice = useMemo(
    () => ({ bundleId, bundlePrice, combines }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundleId, bundlePrice, bundle],
  );

  useEffect(() => {
    if (!accounts.length && Object.keys(storedAccount).length > 0) {
      dispatch(registerAccountStore(storedAccount));
    }
  }, [dispatch, accounts.length, storedAccount]);

  useEffect(() => {
    if (!payBundleHere) setIsIntellipayReady(false);
  }, [payBundleHere]);

  // Without a resolvable selected combine the single-event prices would render
  // as $0.00, and without a valid bundle price the bundle could not be charged
  // correctly, so fall back to the unbundled flow as when there is no bundle.
  const canShowOffer = Boolean(
    bundle && selectedCombine && hasValidBundlePrice(bundle),
  );

  useEffect(() => {
    if (!canShowOffer) {
      navigate("/athlete-membership", {
        state: { fromSpotRegister: true },
        replace: true,
      });
    }
  }, [canShowOffer, navigate]);

  useEffect(() => {
    if (!canShowOffer) return;
    trackEvent("Highschool_ViewedCombineBundleUpsell", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      bundle_price: getBundlePrice(bundle),
      total_separate_price: getTotalSeparatePrice(bundle),
      combine_count: combines.length,
      combines_with_position_drills: getDrillCombineCount(bundle),
      portal_type: "highschool",
    });
  }, [canShowOffer, bundle, eventId, combines.length, trackEvent]);

  if (!canShowOffer) return null;

  const handleDecline = (positionDrill) => {
    trackEvent("Highschool_DeclinedCombineBundle", {
      event_id: eventId,
      bundle_id: getBundleId(bundle),
      position_drill: positionDrill,
      portal_type: "highschool",
    });

    localStorage.removeItem("bundleDetails");

    trackEvent("Highschool_OpenedMembershipPlans", {
      event_id: eventId,
      portal_type: "highschool",
    });

    navigate("/athlete-membership", {
      state: { fromSpotRegister: true },
    });
  };

  // Bundles never carry a coupon, so any coupon keys are left over from an
  // abandoned attempt on the plans screen.
  const handleAccept = () => {
    trackEvent("Highschool_AcceptedCombineBundle", {
      event_id: eventId,
      bundle_id: bundleId,
      bundle_price: bundlePrice,
      portal_type: "highschool",
    });
    localStorage.removeItem("appliedCouponData");
    localStorage.removeItem("appliedCouponCode");
    localStorage.removeItem("appliedCouponType");
  };

  // A bundle price can reach zero (100% off, or an amount off at or above the
  // separate total), so the free bundle completes here without the widget.
  const handleFreeBundle = async () => {
    handleAccept();
    setIsRegisteringFree(true);
    try {
      await registerAndPurchaseCombineBundle(apiInstance, {
        user,
        eventId,
        bundle: bundleChoice,
      });
      trackEvent("Registration5_RegistrationCompleted", {
        event_id: eventId,
        bundle_id: bundleId,
        payment_method: "free",
        portal_type: "highschool",
      });
      toast.success(getCombineBundleSuccessMessage(bundleChoice), {
        position: "top-right",
      });
      clearCombineBundleCheckoutStorage();
      navigate("/register/registrationsuccess");
    } catch (error) {
      toast.error(error.message || "Combine bundle registration failed.", {
        position: "top-right",
      });
    } finally {
      setIsRegisteringFree(false);
    }
  };

  const handleContinue = () => {
    if (isBundleOption) {
      if (bundlePrice === 0) handleFreeBundle();
    } else {
      handleDecline(option === OPTION_SINGLE_WITH_DRILL);
    }
  };

  const handlePayBundleClick = (e) => {
    if (!isIntellipayReady) {
      e.preventDefault();
      toast.info("IntelliPay is loading… please wait.", {
        position: "top-right",
      });
      return;
    }
    handleAccept();
  };

  const continueLabel = {
    [OPTION_SINGLE_WITH_DRILL]: `Single Event + Position Drill – ${formatMoney(singleWithDrillPrice)}`,
    [OPTION_SINGLE]: `Single Event Only – ${formatMoney(singlePrice)}`,
    [OPTION_BUNDLE]: `Continue with Bundle – ${formatMoney(getBundlePrice(bundle))}`,
  }[option];

  return (
    <>
      <Nav />
      <div className="min-h-screen bg-[#363636] w-full px-4 py-8 sm:py-10">
        <div className="w-full max-w-6xl mx-auto bg-white text-gray-900 rounded-lg shadow-xl">
          {/* Header */}
          <div className="px-5 sm:px-10 pt-6 sm:pt-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">
                  Event Registration
                </h1>
                {selectedCombine && (
                  <p className="text-sm sm:text-base text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="uppercase">
                      {getCombineName(selectedCombine)}
                    </span>
                    {getCombineDate(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        <span>{getCombineDate(selectedCombine)}</span>
                      </>
                    )}
                    {getCombineLocation(selectedCombine) && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                        <span>{getCombineLocation(selectedCombine)}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4 sm:gap-8 pt-1 sm:pt-3 flex-shrink-0">
                <span className="text-sm sm:text-base text-gray-800 whitespace-nowrap">
                  Step {ACTIVE_STEP + 1} of {STEPS.length}
                </span>
                <button
                  type="button"
                  onClick={() => handleDecline(false)}
                  title="Close"
                  aria-label="Close"
                  className="text-gray-700 hover:text-gray-900 cursor-pointer"
                >
                  <CloseIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="mt-5">
              <Stepper />
            </div>
          </div>

          {/* Body */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 px-5 sm:px-10 py-6">
            <div className="lg:col-span-3 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Choose Your Event Options
                </h2>
                <span className="inline-flex items-center gap-2 bg-red-600 text-white text-sm font-medium uppercase px-3 py-1 rounded">
                  <FontAwesomeIcon icon={faStar} className="h-3.5 w-3.5" />
                  Best Value
                </span>
              </div>
              <p className="text-gray-700 mt-2">
                Choose one option below. Add more combines and save money with a
                bundle.
              </p>

              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 sm:px-5 py-3 flex items-center gap-4 mt-4">
                <InfoIcon className="h-9 w-9 text-blue-600" />
                <div>
                  <p className="text-gray-900">
                    Your current registration is already included in this
                    bundle.
                  </p>
                  {remainingCombines.length > 0 && (
                    <p className="text-sm text-gray-700">
                      Add {pluralize(remainingCombines.length, "more combine")}
                      {savingsAmount > 0 && (
                        <>
                          {" "}
                          and save{" "}
                          <span className="font-bold">
                            {formatSavings(savingsAmount)}
                          </span>
                        </>
                      )}
                      .
                      {drillCombineCount > 0 &&
                        " All available position drills will be included."}
                    </p>
                  )}
                </div>
              </div>

              {/* Single-event options */}
              <div className="space-y-6 mt-6">
                {selectedHasDrills && (
                  <RadioOption
                    value={OPTION_SINGLE_WITH_DRILL}
                    checked={option === OPTION_SINGLE_WITH_DRILL}
                    onChange={setOption}
                  >
                    <div className="flex justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                          <span className="font-semibold text-lg text-gray-900">
                            Single Event + Position Drill
                          </span>
                          <span className="bg-green-100 text-green-700 text-xs rounded-full px-3 py-0.5">
                            Recommended
                          </span>
                        </div>
                        <p className="text-gray-700 mt-1">
                          Register for this combine and include the position
                          drill.
                        </p>
                      </div>
                      <span className="text-xl font-bold text-gray-900 whitespace-nowrap">
                        {formatMoney(singleWithDrillPrice)}
                      </span>
                    </div>
                    <div className="mt-2">
                      <IncludedChip>
                        Position Drill included (+
                        {formatSavings(selectedDrillPrice)} value)
                      </IncludedChip>
                    </div>
                  </RadioOption>
                )}

                <RadioOption
                  value={OPTION_SINGLE}
                  checked={option === OPTION_SINGLE}
                  onChange={setOption}
                >
                  <div className="flex justify-between gap-4">
                    <div className="min-w-0">
                      <span className="font-semibold text-lg text-gray-900">
                        Single Event Only
                      </span>
                      <p className="text-gray-700 mt-1">
                        {selectedHasDrills
                          ? "Register for this combine without the position drill."
                          : "Register for this combine."}
                      </p>
                    </div>
                    <span className="text-xl font-bold text-gray-900 whitespace-nowrap">
                      {formatMoney(singlePrice)}
                    </span>
                  </div>
                  {selectedHasDrills && (
                    <div className="mt-2">
                      <OptionalChip>
                        Add Position Drill for +
                        {formatSavings(selectedDrillPrice)} (optional)
                      </OptionalChip>
                    </div>
                  )}
                </RadioOption>
              </div>

              {/* Bundle option */}
              <div className="border-t border-gray-200 mt-6 pt-4">
                <label className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="radio"
                    name="eventOption"
                    value={OPTION_BUNDLE}
                    checked={option === OPTION_BUNDLE}
                    onChange={() => setOption(OPTION_BUNDLE)}
                    aria-label="Combine bundle"
                    className="mt-0.5 h-6 w-6 flex-shrink-0 accent-blue-600 cursor-pointer"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-blue-700 uppercase">
                      Add {pluralize(remainingCombines.length, "More Combine")}{" "}
                      To Complete Your Bundle
                    </div>
                    <p className="text-gray-700">
                      {drillCombineCount > 0 ? (
                        <>
                          These combines will include the position drills (where
                          available) at{" "}
                          <span className="text-blue-700">no extra cost</span>.
                        </>
                      ) : (
                        `Includes ${pluralize(remainingCombines.length, "additional combine")}.`
                      )}
                    </p>
                  </div>
                </label>

                <div
                  className={`mt-4 max-h-96 overflow-y-auto space-y-4 rounded-lg ${
                    option === OPTION_BUNDLE ? "ring-2 ring-blue-600 p-2" : ""
                  }`}
                >
                  {selectedCombine && (
                    <BundleCombineRow combine={selectedCombine} isSelected />
                  )}
                  {remainingCombines.map((combine) => (
                    <BundleCombineRow
                      key={getCombineEventId(combine)}
                      combine={combine}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Summary card */}
            <div className="lg:col-span-2 border border-gray-200 rounded-lg px-6 py-6 h-fit">
              <div className="text-center border-b border-gray-200 pb-7">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {getBundleName(bundle)}
                </h3>
                <p className="text-gray-800 mt-1">
                  {pluralize(combineCount, "Combine")}
                  {drillCombineCount > 0 &&
                    ` + ${pluralize(drillCombineCount, "Position Drill")}`}
                </p>
              </div>

              <div className="flex justify-between gap-4 font-bold text-gray-900 text-lg border-b border-gray-200 py-5">
                <span>Total if purchased separately</span>
                <span>{formatMoney(getTotalSeparatePrice(bundle))}</span>
              </div>

              <div className="flex justify-between items-center gap-4 mt-8">
                <span className="text-lg font-bold text-gray-900">
                  Bundle Price
                </span>
                <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {formatMoney(getBundlePrice(bundle))}
                </span>
              </div>

              {savingsAmount > 0 && (
                <div className="bg-green-50 rounded-md px-3 py-4 mt-6 flex items-center justify-center gap-4 text-lg font-semibold text-gray-900">
                  <TagIcon className="h-6 w-6" />
                  <span>
                    You Save {formatSavings(savingsAmount)}
                    {savingsPercent > 0 && ` (${savingsPercent}% Off)`}
                  </span>
                </div>
              )}

              <ul className="text-gray-900 space-y-5 border-t border-gray-200 pt-7 mt-8">
                <li className="flex items-center gap-4">
                  <TrophyIcon className="h-7 w-7" />
                  Maximize your exposure
                </li>
                <li className="flex items-center gap-4">
                  <CalendarIcon className="h-7 w-7" />
                  Compete in {combineCount} high-level events
                </li>
                {drillCombineCount > 0 && (
                  <li className="flex items-center gap-4">
                    <DollarIcon className="h-7 w-7" />
                    All position drills included in bundle
                  </li>
                )}
                <li className="flex items-center gap-4">
                  <UsersIcon className="h-7 w-7" />
                  Best value for serious athletes
                </li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-5 sm:px-10 py-5 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="border-2 border-gray-700 text-gray-900 font-semibold rounded px-6 py-3 hover:bg-gray-50 cursor-pointer flex items-center justify-center gap-3"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Previous
            </button>
            {payBundleHere ? (
              // IntelliPay binds its popup to the ipayfield submit element.
              // Keyed apart from the other button so switching options never
              // leaves the widget's listener on a button that should not pay.
              <button
                key="pay"
                type="button"
                data-ipayname="submit"
                onClick={handlePayBundleClick}
                disabled={!isIntellipayReady}
                aria-busy={!isIntellipayReady}
                className={`ipayfield text-white font-semibold rounded px-8 py-3.5 ${
                  isIntellipayReady
                    ? "bg-blue-700 hover:bg-blue-800 cursor-pointer"
                    : "bg-gray-500 cursor-not-allowed opacity-70"
                }`}
              >
                {isIntellipayReady ? continueLabel : "Loading…"}
              </button>
            ) : (
              <button
                key="continue"
                type="button"
                onClick={handleContinue}
                disabled={isRegisteringFree}
                className={`text-white font-semibold rounded px-8 py-3.5 ${
                  isRegisteringFree
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-800 cursor-pointer"
                }`}
              >
                {isRegisteringFree ? "Registering..." : continueLabel}
              </button>
            )}
          </div>
        </div>
      </div>

      {payBundleHere && (
        <PayWidgetEvents
          userEmail={user?.email}
          userId={user?.userId}
          paymentAmount={bundlePrice}
          eventId={eventId}
          subscriptionType={["combine"]}
          bundle={bundleChoice}
          onIntellipayReady={setIsIntellipayReady}
        />
      )}
    </>
  );
};

export default CombineBundleStep;
